import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Eye, EyeOff, Lock, ShieldOff } from 'lucide-react';
import { retrieveSecret } from '@/api/secrets';
import { ApiError } from '@/api/client';
import type { RetrieveSecretResponse } from '@/types/oscar';
import { CopyButton } from './CopyButton';

// 'cleared'  = the viewer clicked "Clear from screen" — the secret may still be
//              available at the original link (it could have views remaining).
// 'gone'     = the server reported the secret as genuinely unavailable (404/410):
//              already fully viewed, expired, or destroyed.
// 'forbidden' = the secret is locked to a different Onsite account than the one
//               currently signed in (403).
type Phase = 'idle' | 'loading' | 'needsPassphrase' | 'revealed' | 'cleared' | 'gone' | 'forbidden' | 'error';

/**
 * OnPass retrieve page — route /s/:token. Reachable by ANY authenticated user
 * (no Oscar role), matching the PR-22 retrieve endpoint: the high-entropy token
 * is the primary control and the SWA guarantees the opener is signed in.
 *
 * Reveal is explicit (a click), not automatic, so a one-time secret isn't
 * consumed by a link preview, a refresh, or React's double-mount. The plaintext
 * lives only in component state (never localStorage) and can be cleared from the
 * screen.
 */
export function OnPassRetrieve() {
  const { token } = useParams();
  const [phase, setPhase] = useState<Phase>('idle');
  const [passphrase, setPassphrase] = useState('');
  const [result, setResult] = useState<RetrieveSecretResponse | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const reveal = async () => {
    if (!token) return;
    setPhase('loading');
    setMessage(null);
    try {
      const data = await retrieveSecret(token, passphrase.trim() ? passphrase : undefined);
      setResult(data);
      setPhase('revealed');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // Authenticated route → 401 means a passphrase is required / incorrect.
        setPhase('needsPassphrase');
        setMessage(passphrase ? 'That passphrase was incorrect. Try again.' : null);
      } else if (err instanceof ApiError && err.status === 403) {
        // Locked to a different account than the one signed in.
        setPhase('forbidden');
        setMessage(err.message);
      } else if (err instanceof ApiError && (err.status === 404 || err.status === 410)) {
        setPhase('gone');
      } else {
        setPhase('error');
        setMessage(err instanceof ApiError ? err.message : 'Could not retrieve the secret.');
      }
    }
  };

  const clearFromScreen = () => {
    setResult(null);
    setPassphrase('');
    setPhase('cleared');
  };

  return (
    <section className="mx-auto max-w-xl">
      <h1 className="font-heading text-2xl font-bold text-navy">OnPass — view a secret</h1>

      {phase === 'cleared' && (
        <Panel tone="muted" icon={<EyeOff className="h-5 w-5" aria-hidden />}>
          <p className="font-semibold text-navy">This secret has been closed.</p>
          <p className="mt-1 text-sm text-darkGrey">
            It may still be accessible — simply return to the original link to check. If the link
            has expired or reached its view limit, you&rsquo;ll need to ask the sender for a new one.
          </p>
        </Panel>
      )}

      {phase === 'gone' && (
        <Panel tone="muted" icon={<ShieldOff className="h-5 w-5" aria-hidden />}>
          <p className="font-semibold text-navy">This secret is no longer available.</p>
          <p className="mt-1 text-sm text-darkGrey">
            It may have been viewed already, expired, or destroyed. Ask the sender to share a new
            one-time link.
          </p>
        </Panel>
      )}

      {phase === 'forbidden' && (
        <Panel tone="muted" icon={<Lock className="h-5 w-5" aria-hidden />}>
          <p className="font-semibold text-navy">This secret is locked to someone else.</p>
          <p className="mt-1 text-sm text-darkGrey">
            {message ?? 'This secret is locked to a different Onsite account.'} If you believe it was
            meant for you, sign in with the correct account, or ask the sender to check who they
            locked it to.
          </p>
        </Panel>
      )}

      {phase === 'error' && (
        <Panel tone="error">
          <p role="alert" className="text-sm text-red">{message}</p>
          <button type="button" onClick={() => setPhase('idle')} className={btnSecondary}>
            Try again
          </button>
        </Panel>
      )}

      {(phase === 'idle' || phase === 'loading') && (
        <Panel>
          <p className="text-sm text-darkGrey">
            Someone has shared a secret with you. It can be viewed a limited number of times and is
            destroyed afterwards. Reveal it only when you&rsquo;re ready to use it.
          </p>
          <button type="button" onClick={reveal} disabled={phase === 'loading'} className={btnPrimary}>
            <Eye className="h-4 w-4" aria-hidden />
            {phase === 'loading' ? 'Revealing…' : 'Reveal secret'}
          </button>
        </Panel>
      )}

      {phase === 'needsPassphrase' && (
        <Panel icon={<Lock className="h-5 w-5" aria-hidden />}>
          <p className="font-semibold text-navy">This secret is protected by a passphrase.</p>
          {message && <p role="alert" className="mt-1 text-sm text-red">{message}</p>}
          <form
            className="mt-3 flex flex-col gap-2 sm:flex-row"
            onSubmit={(e) => { e.preventDefault(); reveal(); }}
          >
            <input
              type="password"
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              autoComplete="off"
              aria-label="Passphrase"
              placeholder="Enter the passphrase"
              className="flex-1 rounded border border-border bg-white px-2 py-1.5 text-sm"
            />
            <button type="submit" className={btnPrimary}>
              <Eye className="h-4 w-4" aria-hidden />
              Reveal
            </button>
          </form>
        </Panel>
      )}

      {phase === 'revealed' && result && (
        <Panel tone="ok">
          <p className="font-semibold text-navy">Here is the secret — copy it now.</p>
          <p className="mt-1 text-sm text-darkGrey">
            {result.Destroyed
              ? 'This was the last view. The link has now been destroyed and will not work again.'
              : `${result.ViewsRemaining} view${result.ViewsRemaining === 1 ? '' : 's'} remaining after this one.`}
          </p>
          <textarea
            readOnly
            value={result.Secret}
            rows={4}
            aria-label="Revealed secret"
            className="mt-3 w-full rounded border border-border bg-white px-2 py-1.5 font-mono text-sm"
          />
          <div className="mt-2 flex gap-2">
            <CopyButton value={result.Secret} label="Copy secret" />
            <button type="button" onClick={clearFromScreen} className={btnSecondary}>
              Clear from screen
            </button>
          </div>
        </Panel>
      )}
    </section>
  );
}

const btnPrimary =
  'mt-3 inline-flex items-center gap-1.5 rounded bg-orange px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50';
const btnSecondary =
  'mt-3 rounded border border-border bg-white px-4 py-2 text-sm font-medium text-darkGrey hover:bg-bg';

function Panel({
  children,
  tone = 'default',
  icon,
}: {
  children: React.ReactNode;
  tone?: 'default' | 'ok' | 'muted' | 'error';
  icon?: React.ReactNode;
}) {
  const toneCls = {
    default: 'border-border bg-white',
    ok: 'border-green/40 bg-green/10',
    muted: 'border-border bg-bg',
    error: 'border-red/30 bg-red/10',
  }[tone];
  return (
    <div className={`mt-5 rounded-md border p-4 ${toneCls}`}>
      {icon && <div className="mb-2 text-navy">{icon}</div>}
      {children}
    </div>
  );
}
